import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, PauseCircle, PlayCircle, Trash2, Building2, Stethoscope,
  Calendar, CreditCard, Wallet, TrendingUp, Phone, Mail, MapPin, FileText, Users,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatBRL, PLAN_META, STATUS_META, customerFinance } from "@/data/backofficeData";

const Badge = ({ meta }) => (
  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${meta.color}`}>
    {meta.dot && <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />}{meta.label}
  </span>
);

const InfoRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-2">
    <Icon className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="text-sm font-medium text-slate-800">{value}</p>
    </div>
  </div>
);

const MoneyCard = ({ label, value, tint, sub, testid }) => (
  <div data-testid={testid} className={`rounded-2xl border p-5 ${tint}`}>
    <p className="text-[11px] font-semibold uppercase tracking-wide opacity-70">{label}</p>
    <p className="mt-1 font-heading text-2xl font-extrabold font-mono-num">{value}</p>
    {sub && <p className="mt-1 text-xs opacity-70">{sub}</p>}
  </div>
);

export const CustomerDetail = ({ customer: c, setCustomers, onBack, onRemoved }) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fin = customerFinance(c);
  const dateFmt = (s) => new Date(s).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" });

  const togglePause = () => {
    const next = c.status === "pausado" ? "ativo" : "pausado";
    setCustomers((list) => list.map((x) => (x.id === c.id ? { ...x, status: next } : x)));
    toast.success(next === "pausado" ? "Assinatura pausada" : "Assinatura reativada", { description: c.name });
  };

  const remove = () => {
    setCustomers((list) => list.filter((x) => x.id !== c.id));
    toast.success("Assinante removido", { description: c.name });
    setConfirmOpen(false);
    onRemoved();
  };

  return (
    <div className="space-y-5" data-testid="customer-detail">
      <button data-testid="customer-back-btn" onClick={onBack} className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 transition-colors hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Voltar para Clientes Ativos
      </button>

      {/* header */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex items-start gap-4">
            <div className={`flex h-14 w-14 items-center justify-center rounded-2xl ${c.type === "clinica" ? "bg-blue-50 text-blue-600" : "bg-violet-50 text-violet-600"}`}>
              {c.type === "clinica" ? <Building2 className="h-7 w-7" /> : <Stethoscope className="h-7 w-7" />}
            </div>
            <div>
              <h2 className="font-heading text-xl font-extrabold text-slate-900">{c.name}</h2>
              <p className="text-sm text-slate-500">{c.type === "clinica" ? "Clínica" : "Médico(a)"} · {c.specialty}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <Badge meta={PLAN_META[c.plan]} />
                <Badge meta={STATUS_META[c.status]} />
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="detail-pause-btn"
              onClick={togglePause}
              className="flex items-center gap-1.5 rounded-xl border border-slate-200 px-3.5 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50"
            >
              {c.status === "pausado" ? <><PlayCircle className="h-4 w-4 text-emerald-600" />Reativar</> : <><PauseCircle className="h-4 w-4 text-amber-600" />Pausar</>}
            </button>
            <button
              data-testid="detail-remove-btn"
              onClick={() => setConfirmOpen(true)}
              className="flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-3.5 py-2.5 text-sm font-semibold text-rose-600 transition-colors hover:bg-rose-100"
            >
              <Trash2 className="h-4 w-4" />Remover
            </button>
          </div>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Dados completos */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-heading text-sm font-bold text-slate-900">Dados Completos</h3>
          <div className="mt-2 divide-y divide-slate-100">
            <InfoRow icon={FileText} label="Documento" value={c.doc} />
            <InfoRow icon={Users} label="Responsável / Contato" value={`${c.contact} · ${c.doctors} médico(s)`} />
            <InfoRow icon={Phone} label="Telefone" value={c.phone} />
            <InfoRow icon={Mail} label="E-mail" value={c.email} />
            <InfoRow icon={MapPin} label="Endereço" value={c.address} />
          </div>
        </div>

        {/* Assinatura */}
        <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
          <h3 className="font-heading text-sm font-bold text-slate-900">Assinatura</h3>
          <div className="mt-2 divide-y divide-slate-100">
            <InfoRow icon={Calendar} label="Data de assinatura" value={dateFmt(c.since)} />
            <InfoRow icon={CreditCard} label="Plano assinado" value={`VeloClin ${PLAN_META[c.plan].label} · ${formatBRL(c.mrr)}/mês`} />
            <InfoRow icon={TrendingUp} label="Consultas realizadas no mês" value={`${c.consultasMes.toLocaleString("pt-BR")} · ticket médio ${formatBRL(fin.ticket)}`} />
            <InfoRow icon={Wallet} label="Faturamento de consultas" value={formatBRL(fin.faturamentoConsultas)} />
          </div>
        </div>
      </div>

      {/* Repasses / valores */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h3 className="font-heading text-sm font-bold text-slate-900">Repasses & Valores</h3>
        <p className="text-xs text-slate-500">VeloClin recebe a assinatura mensal + {fin.takePct}% sobre as consultas realizadas.</p>
        <div className="mt-4 grid gap-4 sm:grid-cols-3">
          <MoneyCard
            testid="money-veloclin"
            label="Repassado à VeloClin"
            value={formatBRL(fin.veloclinTotal)}
            sub={`Assinatura ${formatBRL(fin.assinatura)} + ${fin.takePct}% consultas (${formatBRL(fin.veloclinConsultas)})`}
            tint="border-slate-900 bg-slate-900 text-white"
          />
          <MoneyCard
            testid="money-medico"
            label={c.type === "clinica" ? "Repasse à clínica" : "Repasse ao médico"}
            value={formatBRL(fin.repasseMedico)}
            sub={`${formatBRL(fin.faturamentoConsultas)} − ${formatBRL(fin.veloclinConsultas)} (taxa VeloClin)`}
            tint="border-emerald-200 bg-emerald-50 text-emerald-700"
          />
          <MoneyCard
            testid="money-consultas"
            label="Faturamento de consultas"
            value={formatBRL(fin.faturamentoConsultas)}
            sub={`${c.consultasMes} consultas × ${formatBRL(fin.ticket)}`}
            tint="border-blue-200 bg-blue-50 text-blue-700"
          />
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="remove-customer-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remover assinante?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove <b>{c.name}</b> da base de assinantes. Não poderá ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="remove-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="remove-confirm" onClick={remove} className="bg-rose-600 hover:bg-rose-700">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
